import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Grid,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Sidebar from '../../Pages/landingPage/Sidebar';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import EditIcon from '../../Images/Edit.png';
import { getOrganizations } from '../../apis/Admin';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import OrganizationDialog from '../OrganizationDialog';
import { AdminTabs } from '../AdminTabs';
import { useTimezone } from '../../hooks/useTimezone';
import OnboardingTour from '../../components/OnboardingTour';
import {
  Person4,
  SettingsRounded,
  TrackChanges,
  Announcement,
  Business,
  AccessTime,
  Groups
} from '@mui/icons-material';
import { DataTable } from '../../components/ui/table';
import { PageHeader } from '../../components/ui/header';
import { PrimaryButton } from '../../components/ui/button';

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, type: 'spring', stiffness: 100 },
  },
};

export default function AdminOrganizations() {
  const navigate = useNavigate();
  const { formatDate } = useTimezone();

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    mode: 'create',
    organization: null,
  });

  const [pageError, setPageError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'none',
  });

  // Super admin check
  const rawRole = useMemo(
    () => Cookies.get('Role_id') ?? Cookies.get('role_id'),
    [],
  );

  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  // Redirect if not super admin
  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/admin/user', { replace: true });
    }
  }, [isSuperAdmin, navigate]);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const orgsData = await getOrganizations();
      setOrganizations(Array.isArray(orgsData) ? orgsData : []);
      setPageError(null);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setPageError('Error fetching organization data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [isSuperAdmin]);

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.column === column && sortConfig.direction === 'asc')
      direction = 'desc';
    else if (sortConfig.column === column && sortConfig.direction === 'desc') {
      direction = 'none';
      column = null;
    }
    setSortConfig({ column, direction });
  };

  const applySort = (rows) => {
    if (sortConfig.direction === 'none' || !sortConfig.column) return rows;
    const arr = [...rows];
    return arr.sort((a, b) => {
      let A, B;
      switch (sortConfig.column) {
        case 'org_name':
          A = (a.org_name || '').toLowerCase();
          B = (b.org_name || '').toLowerCase();
          break;
        case 'plan':
          A = (a.plan_name || '').toLowerCase();
          B = (b.plan_name || '').toLowerCase();
          break;
        case 'seats':
          A = a.total_seats || 0;
          B = b.total_seats || 0;
          break;
        case 'status':
          A = a.is_active ? 'active' : 'inactive';
          B = b.is_active ? 'active' : 'inactive';
          break;
        case 'created_at':
          A = a.created_at || '';
          B = b.created_at || '';
          break;
        default:
          return 0;
      }
      if (sortConfig.column === 'seats' || sortConfig.column === 'created_at') {
        return sortConfig.direction === 'asc' ? A - B : B - A;
      }
      return sortConfig.direction === 'asc'
        ? A.localeCompare(B)
        : B.localeCompare(A);
    });
  };

  const filtered = useMemo(() => {
    const matchesQuery = (org) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;

      const orgName = (org.org_name || '').toLowerCase();
      const description = (org.description || '').toLowerCase();

      return orgName.includes(q) || description.includes(q);
    };

    const rows = organizations.filter(matchesQuery);
    return applySort(rows);
  }, [organizations, search, sortConfig]);

  const displayed = showAll ? filtered : filtered.slice(0, 10);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const total = organizations.length;
    const active = organizations.filter((org) => org.is_active).length;
    const trialExpiring = organizations.filter((org) => {
      if (org.is_paid) return false;
      if (!org.trial_end_date) return false;
      const endDate = new Date(org.trial_end_date);
      const now = new Date();
      const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      return daysRemaining > 0 && daysRemaining <= 14;
    }).length;

    const totalSeats = organizations.reduce((sum, org) => sum + (org.total_seats || 0), 0);
    const usedSeats = organizations.reduce((sum, org) => sum + (org.used_seats || 0), 0);
    const seatUtilization = totalSeats > 0 ? Math.round((usedSeats / totalSeats) * 100) : 0;

    return { total, active, trialExpiring, seatUtilization };
  }, [organizations]);

  const Pill = ({ children, color }) => (
    <Box
      sx={{
        display: 'inline-block',
        px: 1.25,
        py: 0.35,
        borderRadius: '10px',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        color,
        bgcolor: alpha(color, 0.12),
        textTransform: 'capitalize',
      }}
    >
      {children}
    </Box>
  );

  Pill.propTypes = {
    children: PropTypes.node.isRequired,
    color: PropTypes.string.isRequired,
  };

  const getPlanName = (org) => {
    if (org.is_paid) {
      return 'Enterprise';
    }
    return 'Professional';
  };

  const getPlanColor = (org) => {
    if (org.is_paid) {
      return '#9333EA'; // Purple for Enterprise
    }
    return '#3B82F6'; // Blue for Professional
  };

  const getSeatUtilization = (org) => {
    if (org.total_seats === 0) return { percentage: 0, text: 'Unlimited', color: '#6b7280' };
    const percentage = Math.round(((org.used_seats || 0) / org.total_seats) * 100);
    let color = '#2e7d32'; // Green
    if (percentage >= 90) color = '#d32f2f'; // Red
    else if (percentage >= 75) color = '#ed6c02'; // Orange
    return { percentage, text: `${org.used_seats || 0}/${org.total_seats} (${percentage}%)`, color };
  };

  const getTrialDaysLeft = (org) => {
    if (org.is_paid || !org.trial_end_date) return null;
    const endDate = new Date(org.trial_end_date);
    const now = new Date();
    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    return daysRemaining > 0 ? daysRemaining : null;
  };

  if (!isSuperAdmin) {
    return null;
  }

  // Prepare table data
  const tableData = displayed.map((org, index) => ({
    ...org,
    srNo: index + 1,
  }));

  const tableColumns = [
    {
      key: 'org_name',
      label: 'Organization Name',
      sortable: true,
      render: (row) => (
        <Box>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#0F172A',
              fontFamily: 'Inter, sans-serif',
              mb: 0.25,
            }}
          >
            {row.org_name || '—'}
          </Typography>
          {row.description && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#6B7280',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'plan',
      label: 'Plan',
      sortable: true,
      render: (row) => {
        const planName = getPlanName(row);
        const planColor = getPlanColor(row);
        const daysLeft = getTrialDaysLeft(row);
        return (
          <Box>
            <Pill color={planColor}>{planName}</Pill>
            {daysLeft !== null && (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#6B7280',
                  fontFamily: 'Inter, sans-serif',
                  mt: 0.5,
                }}
              >
                {daysLeft} days left
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      key: 'seats',
      label: 'Seats',
      sortable: true,
      render: (row) => {
        const seatInfo = getSeatUtilization(row);
        return (
          <Box sx={{ minWidth: 120 }}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#525252',
                fontFamily: 'Inter, sans-serif',
                mb: 0.5,
              }}
            >
              {seatInfo.text}
            </Typography>
            {row.total_seats > 0 && (
              <LinearProgress
                variant="determinate"
                value={seatInfo.percentage}
                sx={{
                  height: 6,
                  borderRadius: '3px',
                  bgcolor: '#F3F4F6',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: seatInfo.color,
                    borderRadius: '3px',
                  },
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => {
        const statusColor = row.is_active === false ? '#ed6c02' : '#2e7d32';
        const statusText = row.is_active === false ? 'Inactive' : 'Active';
        return <Pill color={statusColor}>{statusText}</Pill>;
      },
    },
    {
      key: 'created_at',
      label: 'Created Date',
      sortable: true,
      render: (row) => (
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: '#525252',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {row.created_at
            ? formatDate(row.created_at, {
                includeTime: false,
              })
            : 'N/A'}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          bgcolor: '#FAFBFD',
          minHeight: '100vh',
        }}
      >
        <Sidebar />
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <PageHeader
            title="Admin"
            createButtonText="+ Create New"
            createButtonClick={() =>
              setDialog({ open: true, mode: 'create', organization: null })
            }
            showSearchIcon={true}
            searchIconClick={() => setSearchOpen(!searchOpen)}
            searchOpen={searchOpen}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search organizations…"
            showConnectorIcon={false}
          />

          <Box sx={{ flexGrow: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
            <Collapse in={!!pageError} unmountOnExit>
              <Alert
                severity="error"
                onClose={() => setPageError(null)}
                sx={{
                  mb: 1,
                  border: '1px solid #fecaca',
                  background: '#fee2e2',
                  color: '#7f1d1d',
                }}
              >
                {pageError}
              </Alert>
            </Collapse>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Navigation Tabs */}
              <Box data-tour="admin-tabs" sx={{ mb: 1.5 }}>
              <AdminTabs
                 items={[
              {
                to: '/admin/organizations',
                label: 'Organizations',
                icon: Business,
              },
              { to: '/admin/user', label: 'User Management', icon: Person4 },
              {
                to: '/admin/annoucement',
                label: 'Annoucement',
                icon: Announcement,
              },
              {
                to: '/admin/setting',
                label: 'Application Settings',
                icon: SettingsRounded,
              },
              {
                to: '/admin/audit-log',
                label: 'Audit Logs',
                icon: TrackChanges,
              },
              ]}
              />
            </Box>

            {/* Section Title */}
            <Box sx={{ mb: 1.5, mt: 1.5 }}>
              <Typography
                sx={{
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                  fontWeight: 500,
                  color: '#4A5565',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                Manage all Organizations and their Subscriptions
              </Typography>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div variants={itemVariants}>
                  <Card
                    sx={{
                      borderRadius: '4px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                      height: '100%',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#4A5565',
                              fontFamily: 'Inter, sans-serif',
                              mb: 0.5,
                            }}
                          >
                            Total Organizations
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '1.25rem',
                              fontWeight: 600,
                              color: '#0F172A',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {summaryMetrics.total}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            bgcolor: '#DBEAFE',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Business sx={{ fontSize: 20, color: '#155DFC' }} />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <motion.div variants={itemVariants}>
                  <Card
                    sx={{
                      borderRadius: '4px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                      height: '100%',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#4A5565',
                              fontFamily: 'Inter, sans-serif',
                              mb: 0.5,
                            }}
                          >
                            Active Organizations
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '1.25rem',
                              fontWeight: 600,
                              color: '#0F172A',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {summaryMetrics.active}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            bgcolor: '#DCFCE7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Business sx={{ fontSize: 20, color: '#059669' }} />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <motion.div variants={itemVariants}>
                  <Card
                    sx={{
                      borderRadius: '4px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                      height: '100%',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#4A5565',
                              fontFamily: 'Inter, sans-serif',
                              mb: 0.5,
                            }}
                          >
                            Trial Expiring Soon
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '1.25rem',
                              fontWeight: 600,
                              color: '#0F172A',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {summaryMetrics.trialExpiring}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            bgcolor: '#FEF3C7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <AccessTime sx={{ fontSize: 20, color: '#F58E01' }} />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <motion.div variants={itemVariants}>
                  <Card
                    sx={{
                      borderRadius: '4px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                      height: '100%',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#4A5565',
                              fontFamily: 'Inter, sans-serif',
                              mb: 0.5,
                            }}
                          >
                            Seat Utilization
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '1.25rem',
                              fontWeight: 600,
                              color: '#0F172A',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {summaryMetrics.seatUtilization}%
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            bgcolor: '#F3E8FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Groups sx={{ fontSize: 20, color: '#9F22FC' }} />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>

            {/* Organizations Table */}
            <Card
              sx={{
                borderRadius: '4px',
                border: '1px solid #E5E7EB',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <DataTable
                  columns={tableColumns}
                  data={tableData}
                  loading={loading}
                  showSerialNumber={true}
                  showActions={true}
                  actionsColumnLabel="Action"
                  renderActions={(row) => (
                    <Tooltip title="Edit Organization">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialog({
                            open: true,
                            mode: 'edit',
                            organization: row,
                          });
                        }}
                        sx={{
                          color: '#6b7280',
                          p: '6px',
                          borderRadius: '6px',
                          '&:hover': {
                            backgroundColor: '#f3f4f6',
                          },
                        }}
                      >
                        <img
                          src={EditIcon}
                          alt="Edit"
                          style={{ width: 20, height: 20 }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  emptyMessage="No organizations found."
                  emptySubMessage={
                    isSuperAdmin
                      ? 'Click "Create Organization" to create one.'
                      : ''
                  }
                  containerSx={{
                    overflowY: 'hidden',
                  }}
                />
              </CardContent>
            </Card>

            {/* View All Button */}
            {filtered.length > 10 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                <PrimaryButton
                  variant="outlined"
                  onClick={() => setShowAll(!showAll)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    px: 3,
                    py: 1,
                  }}
                >
                  {showAll ? 'View Less' : `View All (${filtered.length})`}
                </PrimaryButton>
              </Box>
            )}
          </motion.div>
        </Box>
      </Box>
    </Box>

      <OrganizationDialog
        open={dialog.open}
        mode={dialog.mode}
        organization={dialog.organization}
        onClose={() =>
          setDialog({ open: false, mode: 'create', organization: null })
        }
        onSubmit={async () => {
          await fetchOrganizations();
        }}
      />
      <OnboardingTour />
    </>
  );
}
